"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, type TargetAndTransition } from "motion/react";
import { CloseIcon } from "@/components/icons/icons";
import { cn } from "@/lib/utils";

type AnimationStyle =
  | "from-bottom"
  | "from-center"
  | "from-top"
  | "from-left"
  | "from-right"
  | "fade"
  | "top-in-bottom-out"
  | "left-in-right-out";

type VideoSource = { src: string; type: string; media?: string };

interface HeroVideoDialogProps {
  videoSrc: string;
  posterSrc?: string;
  videoAlt?: string;
  animationStyle?: AnimationStyle;
  className?: string;
  /**
   * Optional multi-source list for the always-on background <video>. When
   * provided, the inline player uses these <source> elements (with media
   * queries for responsive selection) and applies fetchpriority="high" so
   * the asset is treated as LCP. The single `videoSrc` prop is still used
   * for the modal player opened on click.
   */
  inlineSources?: readonly VideoSource[];
}

const animationVariants: Record<
  AnimationStyle,
  { initial: TargetAndTransition; animate: TargetAndTransition; exit: TargetAndTransition }
> = {
  "from-bottom": {
    initial: { y: "100%", opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: "100%", opacity: 0 },
  },
  "from-center": {
    initial: { scale: 0.5, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.5, opacity: 0 },
  },
  "from-top": {
    initial: { y: "-100%", opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: "-100%", opacity: 0 },
  },
  "from-left": {
    initial: { x: "-100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "-100%", opacity: 0 },
  },
  "from-right": {
    initial: { x: "100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "100%", opacity: 0 },
  },
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  "top-in-bottom-out": {
    initial: { y: "-100%", opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: "100%", opacity: 0 },
  },
  "left-in-right-out": {
    initial: { x: "-100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "100%", opacity: 0 },
  },
};

export function HeroVideoDialog({
  videoSrc,
  posterSrc,
  videoAlt = "Hero video",
  animationStyle = "from-center",
  className,
  inlineSources,
}: HeroVideoDialogProps) {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const variant = animationVariants[animationStyle];

  useEffect(() => {
    setMounted(true);
  }, []);

  // Mobile browsers (iOS Safari especially) often ignore the `autoPlay`
  // attribute when battery/data-saver modes are active or when the element
  // mounts before its <source> children resolve. Force-call .play() and
  // re-attempt on canplay/loadedmetadata so autoplay is robust across browsers.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    const tryPlay = () => {
      el.play().catch(() => {});
    };
    tryPlay();
    el.addEventListener("loadedmetadata", tryPlay);
    el.addEventListener("canplay", tryPlay);
    return () => {
      el.removeEventListener("loadedmetadata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
    };
  }, []);

  useEffect(() => {
    if (!isVideoOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsVideoOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [isVideoOpen]);

  const dialog = (
    <AnimatePresence>
      {isVideoOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsVideoOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={videoAlt}
          className="fixed inset-0 z-[100] flex cursor-pointer items-center justify-center bg-neutral-950/40 p-4 backdrop-blur-md sm:p-8 lg:p-12"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsVideoOpen(false);
            }}
            aria-label="Close video"
            className="absolute right-4 top-4 z-10 flex size-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/30 backdrop-blur-md transition-colors hover:bg-white/20 sm:right-6 sm:top-6"
          >
            <CloseIcon className="size-5" />
          </button>
          <motion.div
            {...variant}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative mx-auto aspect-video w-full max-w-5xl cursor-auto"
          >
            <video
              src={videoSrc}
              poster={posterSrc}
              autoPlay
              controls
              playsInline
              className="size-full rounded-2xl bg-black object-contain shadow-[0_30px_70px_-22px_rgba(0,0,0,0.45)] ring-1 ring-white/10"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setIsVideoOpen(true)}
        aria-label={videoAlt}
        className="block w-full cursor-pointer overflow-hidden rounded-[inherit]"
      >
        {inlineSources && inlineSources.length > 0 ? (
          <video
            ref={videoRef}
            poster={posterSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            // React 19 supports fetchPriority on <img>/<link>, but the type
            // hasn't propagated to VideoHTMLAttributes yet — cast to satisfy TS.
            {...({ fetchPriority: "high" } as { fetchPriority: "high" })}
            className="h-auto w-full"
          >
            {inlineSources.map((s) => (
              <source key={s.src} src={s.src} type={s.type} media={s.media} />
            ))}
          </video>
        ) : (
          <video
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="h-auto w-full"
          />
        )}
      </button>
      {mounted ? createPortal(dialog, document.body) : null}
    </div>
  );
}
